import React, { useState, useEffect } from 'react'
import { makeApiCall, makeJsonApiCall } from '../utils/customerUtils'

interface Customer {
  id: number
  name: string | null
  first_name: string | null
  last_name: string | null
  email: string
  phone: string | null
  created_at: string
  broker_name: string
  needs_migration: boolean
}

interface CustomersByBroker {
  [brokerEmail: string]: Customer[]
}

const CustomerNameMigration: React.FC = () => {
  const [customersByBroker, setCustomersByBroker] = useState<CustomersByBroker>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [savingCustomers, setSavingCustomers] = useState<Set<number>>(new Set())
  const [editingCustomer, setEditingCustomer] = useState<number | null>(null)
  const [tempNames, setTempNames] = useState<{ [customerId: number]: { firstName: string, lastName: string } }>({})

  useEffect(() => {
    fetchCustomers()
  }, [])

  const fetchCustomers = async () => {
    try {
      setLoading(true)
      setError(null)

      const response = await makeApiCall('api/admin/customer-name-migration')
      if (!response.ok) {
        throw new Error('Failed to fetch customers')
      }

      const data = await response.json()
      setCustomersByBroker(data.customersByBroker || {})
    } catch (err: any) {
      console.error('Error fetching customers:', err)
      setError(err.message || 'Failed to fetch customers')
    } finally {
      setLoading(false)
    }
  }

  const startEditing = (customer: Customer) => {
    setEditingCustomer(customer.id)
    
    // If the customer already has first/last names, use them; otherwise try to split the name
    if (customer.first_name && customer.last_name) {
      setTempNames({
        ...tempNames,
        [customer.id]: {
          firstName: customer.first_name,
          lastName: customer.last_name
        }
      })
    } else if (customer.name) {
      const nameParts = customer.name.trim().split(' ')
      const firstName = nameParts[0] || ''
      const lastName = nameParts.slice(1).join(' ') || ''
      
      setTempNames({
        ...tempNames,
        [customer.id]: {
          firstName,
          lastName
        }
      })
    } else {
      setTempNames({
        ...tempNames,
        [customer.id]: {
          firstName: '',
          lastName: ''
        }
      })
    }
  }

  const cancelEditing = () => {
    setEditingCustomer(null)
    setTempNames({})
  }

  const saveCustomerNames = async (customerId: number) => {
    const names = tempNames[customerId]
    if (!names || !names.firstName.trim() || !names.lastName.trim()) {
      alert('Both first name and last name are required')
      return
    }

    try {
      setSavingCustomers(prev => new Set(prev).add(customerId))

      const response = await makeJsonApiCall(`api/admin/customer-name-migration/${customerId}`, 'PUT', {
        first_name: names.firstName.trim(),
        last_name: names.lastName.trim()
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to update customer names')
      }

      // Refresh the customer list
      await fetchCustomers()
      setEditingCustomer(null)
      setTempNames({})
      
    } catch (err: any) {
      console.error('Error saving customer names:', err)
      alert(`Error: ${err.message}`)
    } finally {
      setSavingCustomers(prev => {
        const newSet = new Set(prev)
        newSet.delete(customerId)
        return newSet
      })
    }
  }

  const updateTempName = (customerId: number, field: 'firstName' | 'lastName', value: string) => {
    setTempNames(prev => ({
      ...prev,
      [customerId]: {
        ...prev[customerId],
        [field]: value
      }
    }))
  }

  const getDisplayName = (customer: Customer) => {
    if (customer.first_name && customer.last_name) {
      return `${customer.first_name} ${customer.last_name}`
    }
    return customer.name || 'Unknown'
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600 dark:text-gray-400">Loading customers...</div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
          <div className="text-red-800 dark:text-red-200 font-medium">Error</div>
          <div className="text-red-600 dark:text-red-400">{error}</div>
          <button
            onClick={fetchCustomers}
            className="mt-2 px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  const totalCustomers = Object.values(customersByBroker).flat().length
  const needingMigration = Object.values(customersByBroker).flat().filter(c => c.needs_migration).length

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
          Customer Name Migration
        </h1>
        <p className="text-gray-600 dark:text-gray-300">
          Manually migrate customer names from the old single name field to first and last name fields.
        </p>
        <div className="mt-4 flex space-x-4 text-sm">
          <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded">
            Total Customers: {totalCustomers}
          </span>
          <span className="px-3 py-1 bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 rounded">
            Needing Migration: {needingMigration}
          </span>
        </div>
      </div>

      <div className="space-y-8">
        {Object.entries(customersByBroker).map(([brokerEmail, customers]) => (
          <div key={brokerEmail} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Broker: {brokerEmail}
            </h2>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Current Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      First Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Last Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {customers.map((customer) => (
                    <tr key={customer.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                        {getDisplayName(customer)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                        {customer.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {customer.needs_migration ? (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200">
                            Needs Migration
                          </span>
                        ) : (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                            Migrated
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {editingCustomer === customer.id ? (
                          <input
                            type="text"
                            value={tempNames[customer.id]?.firstName || ''}
                            onChange={(e) => updateTempName(customer.id, 'firstName', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm dark:bg-gray-700 dark:text-white"
                            placeholder="First name"
                          />
                        ) : (
                          <span className="text-sm text-gray-900 dark:text-gray-100">
                            {customer.first_name || '-'}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {editingCustomer === customer.id ? (
                          <input
                            type="text"
                            value={tempNames[customer.id]?.lastName || ''}
                            onChange={(e) => updateTempName(customer.id, 'lastName', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm dark:bg-gray-700 dark:text-white"
                            placeholder="Last name"
                          />
                        ) : (
                          <span className="text-sm text-gray-900 dark:text-gray-100">
                            {customer.last_name || '-'}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {editingCustomer === customer.id ? (
                          <div className="flex space-x-2">
                            <button
                              onClick={() => saveCustomerNames(customer.id)}
                              disabled={savingCustomers.has(customer.id)}
                              className="px-3 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:opacity-50"
                            >
                              {savingCustomers.has(customer.id) ? 'Saving...' : 'Save'}
                            </button>
                            <button
                              onClick={cancelEditing}
                              className="px-3 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-700"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => startEditing(customer)}
                            className="px-3 py-1 bg-gray-900 text-white rounded text-xs hover:bg-gray-800"
                          >
                            {customer.needs_migration ? 'Migrate' : 'Edit'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>

      {Object.keys(customersByBroker).length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-500 dark:text-gray-400">No customers found</div>
        </div>
      )}
    </div>
  )
}

export default CustomerNameMigration 